import React, { useEffect, useMemo, useRef, useState } from "react";

// Однофайловый лендинг «ЛипидПро». Две темы: light (дефолт) и dark.
// Переключение: ?theme=light | <meta name="theme" content="light"> | <html data-theme="light"> | window.__THEME__="light"

/**
 * WAITLIST ENDPOINT (приоритет сверху вниз):
 * 1) window.__WAITLIST_ENDPOINT__ = "https://api.example.ru/public/waitlist"
 * 2) <meta name="waitlist-endpoint" content="..." />
 * 3) <html data-waitlist-endpoint="...">
 * 4) process.env.NEXT_PUBLIC_WAITLIST_ENDPOINT
 * 5) process.env.REACT_APP_WAITLIST_ENDPOINT
 * 6) иначе: "" (локальное сохранение заявок в localStorage)
 *
 * Путь к превью‑скринам (приоритет):
 * A) window.__PREVIEW_IMAGES__ = ["url1","url2","url3"]
 * B) <meta name="preview-image-1" content="..."> (‑2,‑3)
 * C) <html data-preview-image-1="...">
 *
 * Вид рамки скриншотов (приоритет):
 * 1) ?screensView=plain|iphone
 * 2) window.__SCREENS_VIEW__ = "plain" | "iphone"
 * 3) <meta name="screens-view" content="plain|iphone">
 * 4) <html data-screens-view="plain|iphone">
 * 5) VITE_SCREENS_VIEW / NEXT_PUBLIC_SCREENS_VIEW / REACT_APP_SCREENS_VIEW
 */

declare global {
  interface Window {
    __WAITLIST_ENDPOINT__?: string;
    __PREVIEW_IMAGES__?: string[];
    __SCREENS_MODE__?: string;
    __SCREENS_VIEW__?: string;
    __THEME__?: string;
  }
}

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

function firstNonEmpty<T extends string | undefined | null>(...values: Array<T>): string {
  for (const v of values) if (typeof v === "string" && v.trim()) return v.trim();
  return "";
}
function readMeta(name: string): string {
  if (!isBrowser) return "";
  const tag = document.querySelector(`meta[name="${name}"]`);
  return (tag?.getAttribute("content") || "").trim();
}
function readHtmlDataAttr(attr: string): string {
  if (!isBrowser) return "";
  return (document.documentElement?.getAttribute(attr) || "").trim();
}
function getWaitlistEndpoint(): string {
  const fromGlobal = isBrowser ? window.__WAITLIST_ENDPOINT__ : "";
  const fromMeta = readMeta("waitlist-endpoint");
  const fromData = readHtmlDataAttr("data-waitlist-endpoint");
  const fromVite = (import.meta.env?.VITE_WAITLIST_ENDPOINT as string | undefined) ?? "";
  const fromNext = typeof process !== "undefined" ? (process as any)?.env?.NEXT_PUBLIC_WAITLIST_ENDPOINT as string : "";
  const fromCRA  = typeof process !== "undefined" ? (process as any)?.env?.REACT_APP_WAITLIST_ENDPOINT as string : "";
  return firstNonEmpty(fromGlobal, fromMeta, fromData, fromVite, fromNext, fromCRA);
}
function getPreviewImages(max = 6): string[] {
  const list: string[] = [];
  if (isBrowser && Array.isArray(window.__PREVIEW_IMAGES__)) {
    for (const u of window.__PREVIEW_IMAGES__) {
      if (typeof u === "string" && u.trim()) list.push(u.trim());
      if (list.length>=max) return list;
    }
  }
  for (let i=1;i<=max;i++){ const m = readMeta(`preview-image-${i}`); if (m) list.push(m); }
  if (list.length>=max) return list.slice(0,max);
  for (let i=1;i<=max && list.length<max;i++){ const d = readHtmlDataAttr(`data-preview-image-${i}`); if (d) list.push(d); }
  return list.slice(0,max);
}
function getScreensMode(): 'images' | 'chat' {
  const fromGlobal = isBrowser ? (window.__SCREENS_MODE__ || "") : "";
  const fromMeta = readMeta("screens-mode");
  const fromData = readHtmlDataAttr("data-screens-mode");
  const mode = firstNonEmpty(fromGlobal, fromMeta, fromData).toLowerCase();
  return mode === 'images' ? 'images' : 'chat';
}
type ScreensView = "plain" | "iphone";
function getScreensView(): ScreensView {
  const fromQuery = isBrowser ? (new URLSearchParams(window.location.search).get("screensView") || "") : "";
  const fromGlobal = isBrowser ? (window.__SCREENS_VIEW__ || "") : "";
  const fromMeta = readMeta("screens-view");
  const fromData = readHtmlDataAttr("data-screens-view");
  const fromVite = (import.meta.env?.VITE_SCREENS_VIEW as string | undefined) ?? "";
  const fromNext = typeof process !== "undefined" ? (process as any)?.env?.NEXT_PUBLIC_SCREENS_VIEW as string : "";
  const fromCRA  = typeof process !== "undefined" ? (process as any)?.env?.REACT_APP_SCREENS_VIEW as string : "";
  const view = firstNonEmpty(fromQuery, fromGlobal, fromMeta, fromData, fromVite, fromNext, fromCRA).toLowerCase();
  return view === "iphone" ? "iphone" : "plain";
}

// ===== Тема оформления =====
export type ThemeMode = 'light' | 'dark';
function getTheme(): ThemeMode {
  const q = isBrowser ? new URLSearchParams(window.location.search).get('theme') : '';
  const fromGlobal = isBrowser ? (window.__THEME__ || '') : '';
  const fromMeta = readMeta('theme');
  const fromData = readHtmlDataAttr('data-theme');
  const mode = firstNonEmpty(q || '', fromGlobal, fromMeta, fromData).toLowerCase();
  return mode === 'dark' ? 'dark' : 'light';
}
function tw(theme: ThemeMode){
  if(theme==='light'){
    return {
      wrapper: 'min-h-screen bg-[#F6F8FB] text-slate-900',
      header: 'sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-slate-200',
      navText: 'text-slate-600',
      sectionBorder: 'border-slate-200',
      card: 'rounded-3xl border border-slate-200 bg-white p-6 shadow-sm',
      cardSm: 'rounded-2xl border border-slate-200 bg-white p-4 shadow-sm',
      subtle: 'text-slate-500',
      muted: 'text-slate-600',
      body: 'text-slate-800',
      track: 'bg-slate-200',
      cta: 'bg-green-500 text-white',
      ghost: 'border-slate-300 text-slate-700 hover:bg-slate-50',
      pillBg: 'bg-slate-50',
      pillBorder: 'border-slate-200',
      progressRose: 'bg-red-500',
      accentBadge: 'bg-green-500 text-white'
    } as const;
  }
  return {
    wrapper: 'min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white',
    header: 'sticky top-0 z-40 backdrop-blur bg-slate-950/60 border-b border-white/10',
    navText: 'text-white/70',
    sectionBorder: 'border-white/10',
    card: 'rounded-3xl border border-white/10 bg-white/5 p-6',
    cardSm: 'rounded-2xl border border-white/10 bg-white/5 p-4',
    subtle: 'text-white/50',
    muted: 'text-white/70',
    body: 'text-white/80',
    track: 'bg-white/10',
    cta: 'bg-lime-400 text-slate-900',
    ghost: 'border-white/20 text-white/80 hover:bg-white/5',
    pillBg: 'bg-white/5',
    pillBorder: 'border-white/10',
    progressRose: 'bg-rose-400',
    accentBadge: 'bg-lime-400 text-slate-900'
  } as const;
}

// ===== Примеры автоматической расшифровки =====
 type AIDemo = {
  level: "Высокий" | "Умеренный" | "Низкий";
  score: number; tint: "rose" | "amber" | "emerald";
  summary: string; lipids: { tc:number; ldl:number; hdl:number; tg:number };
  factors: string[]; advice: string[];
};
function buildAIDemoSamples(): AIDemo[] {
  return [
    { level:"Высокий", score:29, tint:"rose",
      summary:"Выраженные нарушения липидного обмена на фоне артериальной гипертензии. Рекомендуется срочная консультация врача и коррекция терапии.",
      lipids:{tc:6.2, ldl:3.5, hdl:1.0, tg:2.2},
      factors:["Артериальная гипертензия","Наследственность"],
      advice:["Обсудить результаты и план наблюдения с лечащим врачом","Снизить долю насыщенных жиров и быстрых углеводов","Контролировать артериальное давление и повторить липидограмму в согласованные с врачом сроки"] },
    { level:"Умеренный", score:62, tint:"amber",
      summary:"Промежуточный риск: уровень липопротеинов низкой плотности слегка выше целевого, есть положительная динамика. Требуется усиление немедикаментозных мер.",
      lipids:{tc:5.2, ldl:2.8, hdl:1.3, tg:1.6},
      factors:["Избыточная масса тела","Низкая активность"],
      advice:["Увеличить потребление клетчатки, рыбы 2–3 раза/нед","150–300 мин аэробной активности в неделю","Переоценка рисков и целевых уровней липопротеинов низкой плотности"] },
    { level:"Низкий", score:88, tint:"emerald",
      summary:"Целевые уровни липидов достигнуты, устойчивое улучшение. Важно поддерживать образ жизни и контроль показателей.",
      lipids:{tc:4.3, ldl:1.6, hdl:1.5, tg:1.1},
      factors:["Высокая приверженность","Регулярная активность"],
      advice:["Сохранять текущую терапию/образ жизни","Контроль липидограммы раз в 3–6 месяцев","Поддерживать массу тела и уровень активности"] },
  ];
}

function AIAssessmentMini({theme}:{theme:ThemeMode}){
  const t = tw(theme);
  const center = theme==='light' ? 'bg-white text-slate-800' : 'bg-slate-900/95 text-white/80';
  return (
    <div className={`mt-4 ${t.cardSm}`}>
      <div className="flex items-center gap-4">
        <div aria-hidden className="relative h-20 w-20 rounded-full" style={{backgroundImage:"conic-gradient(#fb7185 0 120deg,#60a5fa 120deg 240deg,#facc15 240deg)"}}>
          <div className={`absolute inset-2 rounded-full flex items-center justify-center text-[10px] font-semibold ${center}`}>ВЫСОКИЙ</div>
        </div>
        <div className={`grid grid-cols-2 gap-x-4 gap-y-1 text-xs ${t.muted}`}>
          <div className="flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full bg-rose-400"/>Сосудистый риск</div>
          <div className="flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full bg-sky-400"/>Факторы риска</div>
          <div className="flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full bg-amber-300"/>Липидный обмен</div>
          <div className="flex items-center gap-2"><span className="inline-block h-2 w-2 rounded-full bg-black/20"/>Прочее</div>
        </div>
      </div>
      <p className={`mt-3 text-xs ${t.muted}`}>Нарушение липидного обмена и АГ. Требуется консультация врача и коррекция терапии.</p>
      <div className="mt-3">
        <div className={`flex items-center justify-between text-[11px] ${t.muted} mb-1`}><span>Health Index</span><span>29/100</span></div>
        <div className={`h-2 w-full rounded-full ${t.track}`}><div className={`h-2 rounded-full ${tw(theme).progressRose}`} style={{width:"29%"}}/></div>
      </div>
    </div>
  );
}

function DiaryMini({theme}:{theme:ThemeMode}){
  const t = tw(theme);
  const MacroBar = ({label,value}:{label:'Б'|'Ж'|'У';value:number}) => (
    <div className={`flex items-center gap-2 text-[11px] ${t.muted}`}><span className="w-10">{label}</span><div className={`h-2 flex-1 rounded-full ${t.track}`}><div className={`h-2 rounded-full ${getMacroColorClass(label)}`} style={{width:`${Math.min(100,value)}%`}}/></div></div>
  );
  const MealItem = ({title,desc,kcal,icon}:{title:string;desc:string;kcal:number;icon:string}) => (
    <div className="flex items-center gap-3"><div role="img" aria-label={`Фото: ${title}`} className={`h-10 w-10 rounded-lg border flex items-center justify-center text-lg ${theme==='light' ? 'bg-slate-50 border-slate-200' : 'bg-gradient-to-br from-white/10 to-white/5 border-white/10'}`}><span aria-hidden>{icon}</span></div><div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{title}</div><div className={`text-xs truncate ${t.muted}`}>{desc}</div></div><div className={`text-xs ${t.muted}`}>{kcal} ккал</div></div>
  );
  const ScanBtn = () => (
    <button className={`inline-flex items-center gap-2 rounded-full text-xs font-semibold px-3 py-2 hover:brightness-95 ${t.cta}`}><span aria-hidden>📷</span>ИИ‑сканер</button>
  );
  return (
    <div className={`mt-4 relative overflow-hidden ${t.cardSm}`}>
      <div className="grid grid-cols-2 gap-3"><div><div className={`text-xs mb-1 ${t.muted}`}>Сегодня</div><div className="text-lg font-semibold">1 280 ккал</div></div><div className="space-y-1"><MacroBar label="Б" value={60}/><MacroBar label="Ж" value={45}/><MacroBar label="У" value={72}/></div></div>
      <div className="mt-3 space-y-2"><MealItem title="Завтрак" desc="Овсянка, йогурт, ягоды" kcal={320} icon="🥣"/><MealItem title="Обед" desc="Курица, киноа, салат" kcal={520} icon="🥗"/></div>
      <div className="mt-3 flex items-center justify-between"><div className={`text-xs ${t.muted}`}>Сканирование тарелки по фото (ИИ)</div><ScanBtn/></div>
      <div className={`mt-4 pt-4 border-t ${t.sectionBorder}`}>
        <div className="flex items-center justify-between"><div className="text-sm font-medium">Достижения и LP‑баллы</div><div className={`text-xs ${t.muted}`}>Баланс: <span className="font-semibold">120 LP</span></div></div>
        <div className="mt-2 grid grid-cols-1 gap-2">
          <div className={`flex items-center justify-between rounded-xl px-3 py-2 border ${t.pillBorder} ${t.pillBg}`}><span className={`text-xs ${t.muted}`}>Ежедневный вход (серия без пропусков)</span><span className={`text-[11px] px-2 py-1 rounded-full ${t.accentBadge}`}>+10 LP</span></div>
          <div className={`flex items-center justify-between rounded-xl px-3 py-2 border ${t.pillBorder} ${t.pillBg}`}><span className={`text-xs ${t.muted}`}>Добавлена липидограмма</span><span className={`text-[11px] px-2 py-1 rounded-full ${t.accentBadge}`}>+50 LP</span></div>
          <div className={`flex items-center justify-between rounded-xl px-3 py-2 border ${t.pillBorder} ${t.pillBg}`}><span className={`text-xs ${t.muted}`}>Неделя осознанного питания</span><span className={`text-[11px] px-2 py-1 rounded-full ${t.accentBadge}`}>+30 LP</span></div>
        </div>
        <div className="mt-3"><div className={`flex items-center justify-between text-[11px] mb-1 ${t.muted}`}><span>До бесплатного месяца</span><span>60/100 LP</span></div><div className={`h-2 w-full rounded-full ${t.track}`}><div className="h-2 rounded-full bg-lime-400" style={{width:"60%"}}/></div><div className={`mt-2 text-[11px] ${t.muted}`}>На что потратить: <span className="font-medium">скидка на подписку, консультация врача, мерч, благотворительность</span></div></div>
      </div>
      <div className={`absolute bottom-3 right-3 h-10 w-10 rounded-full font-bold flex items-center justify-center shadow-lg ${t.accentBadge.replace(' text-white',' text-slate-900')}`}>+</div>
    </div>
  );
}

function AIDemoSection({theme}:{theme:ThemeMode}){
  const demos = buildAIDemoSamples();
  const t = tw(theme);
  const tintToBar  = (x:AIDemo['tint']) => x==='rose'? 'bg-rose-400' : x==='amber'? 'bg-amber-400':'bg-emerald-400';
  const pillTone = (x:AIDemo['tint']) => x==='rose'? 'text-rose-600 border-rose-300' : x==='amber'? 'text-amber-700 border-amber-300':'text-emerald-700 border-emerald-300';
  return (
    <section id="ai-demo" className={`py-8 sm:py-12 border-t ${t.sectionBorder}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl sm:text-4xl font-bold">Примеры автоматической расшифровки липидограммы</h2>
        <p className={`${t.muted} text-sm mt-2 max-w-3xl`}>Показываем, как сервис объясняет результаты липидограммы простым языком: общий холестерин, липопротеины низкой и высокой плотности, триглицериды и динамика показателей.</p>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {demos.map((d,i)=> (
            <div key={i} className={`${t.card}`}>
              <div className="flex items-center justify-between"><div className="text-lg font-semibold">{d.level} риск</div><div className={`text-xs px-2 py-1 rounded-full border ${pillTone(d.tint)} uppercase tracking-wide`}>Health Index: {d.score}/100</div></div>
              <div className="mt-3"><div className={`h-2 w-full rounded-full ${t.track}`}><div className={`h-2 rounded-full ${tintToBar(d.tint)}`} style={{width:`${d.score}%`}}/></div></div>
              <p className={`mt-4 text-sm ${t.body}`}>{d.summary}</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className={`${theme==='light'?'bg-slate-50':'bg-white/5'} rounded-xl ${theme==='light'?'border border-slate-200':''} p-3`}><div className={`${t.subtle}`}>ОХС</div><div className="font-semibold">{d.lipids.tc}</div></div>
                <div className={`${theme==='light'?'bg-slate-50':'bg-white/5'} rounded-xl ${theme==='light'?'border border-slate-200':''} p-3`}><div className={`${t.subtle}`}>ЛПНП</div><div className="font-semibold">{d.lipids.ldl}</div></div>
                <div className={`${theme==='light'?'bg-slate-50':'bg-white/5'} rounded-xl ${theme==='light'?'border border-slate-200':''} p-3`}><div className={`${t.subtle}`}>ЛПВП</div><div className="font-semibold">{d.lipids.hdl}</div></div>
                <div className={`${theme==='light'?'bg-slate-50':'bg-white/5'} rounded-xl ${theme==='light'?'border border-slate-200':''} p-3`}><div className={`${t.subtle}`}>ТГ</div><div className="font-semibold">{d.lipids.tg}</div></div>
              </div>
              <div className="mt-4"><div className={`${t.subtle} text-xs mb-1`}>Ключевые факторы</div><div className="flex flex-wrap gap-2">{d.factors.map((f,idx)=>(<span key={idx} className={`text-xs px-2 py-1 rounded-full ${t.pillBg} border ${t.pillBorder} ${t.muted}`}>{f}</span>))}</div></div>
              <div className="mt-4"><div className={`${t.subtle} text-xs mb-1`}>Рекомендации</div><ul className={`list-disc list-inside text-sm ${t.body} space-y-1`}>{d.advice.map((a,idx)=>(<li key={idx}>{a}</li>))}</ul></div>
            </div>
          ))}
        </div>
        <p className={`${t.subtle} text-xs mt-4`}>* Примеры носят информационный характер и не являются медицинской рекомендацией или диагнозом. Расшифровка не заменяет очную консультацию специалиста.</p>
      </div>
    </section>
  );
}

const WAITLIST_ENDPOINT = getWaitlistEndpoint();
const PREVIEW_IMAGES_RAW = getPreviewImages(3);
const DEFAULT_PREVIEW_IMAGES = [
  "/screens/screen1.png", // Главный экран
  "/screens/screen2.png", // Липидный профиль
  "/screens/screen3.png", // Дневник питания
];
const DEFAULT_CAPTIONS = ["Главный экран","Липидный профиль","Дневник питания"];
const PREVIEW_IMAGES = PREVIEW_IMAGES_RAW.length ? PREVIEW_IMAGES_RAW : DEFAULT_PREVIEW_IMAGES;
const DISH_PHOTO = "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1200&q=80";

// Нейтральная рамка без имитации устройства
function PlainScreenshotFrame({src, alt, theme}:{src:string; alt:string; theme:ThemeMode}){
  const outer = theme==='light'
    ? 'bg-white border-slate-200 shadow-sm'
    : 'bg-white/5 border-white/10';
  const preventImageSave = (e: React.SyntheticEvent) => e.preventDefault();
  return (
    <div
      className={`mx-auto w-full max-w-[320px] rounded-2xl border p-2 ${outer}`}
      onContextMenu={preventImageSave}
    >
      <div className={`relative aspect-[1179/2556] overflow-hidden rounded-2xl ${theme==='light' ? 'bg-slate-50' : 'bg-slate-900'}`}>
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-contain select-none"
          loading="lazy"
          draggable={false}
          onContextMenu={preventImageSave}
          onDragStart={preventImageSave}
          style={{ WebkitTouchCallout: "none", userSelect: "none" }}
        />
      </div>
    </div>
  );
}
function IPhoneDynamicIslandFrame({src, alt, theme}:{src:string; alt:string; theme:ThemeMode}){
  const preventImageSave = (e: React.SyntheticEvent) => e.preventDefault();
  const shell = theme === "light"
    ? "bg-slate-900 border-slate-800 shadow-[0_20px_45px_rgba(15,23,42,0.25)]"
    : "bg-black border-white/20 shadow-[0_20px_45px_rgba(0,0,0,0.45)]";
  const screenBg = theme === "light" ? "bg-black" : "bg-slate-950";
  return (
    <div
      className="mx-auto w-full max-w-[320px]"
      onContextMenu={preventImageSave}
    >
      <div className={`relative rounded-[2.4rem] border-[6px] p-[7px] ${shell}`}>
        <div className={`relative overflow-hidden rounded-[2rem] aspect-[1179/2556] ${screenBg}`}>
          <div className="pointer-events-none absolute left-1/2 top-2 z-20 h-8 w-[44%] -translate-x-1/2 rounded-full bg-black/90 ring-1 ring-white/10" />
          <img
            src={src}
            alt={alt}
            className="relative z-10 h-full w-full object-cover select-none"
            loading="lazy"
            draggable={false}
            onContextMenu={preventImageSave}
            onDragStart={preventImageSave}
            style={{ WebkitTouchCallout: "none", userSelect: "none" }}
          />
        </div>
      </div>
    </div>
  );
}

export default function LandingEarlyAccess(){
  const [showModal,setShowModal] = useState(false);
  const [email,setEmail] = useState("");
  const [consent,setConsent] = useState(false);
  const [loading,setLoading] = useState(false);
  const [status,setStatus] = useState<null | {ok:boolean; msg:string}>(null);
  const [copied,setCopied] = useState(false);
  const formRef = useRef<HTMLFormElement|null>(null);

  // Простая маршрутизация для /privacy
  const [path, setPath] = useState(isBrowser ? window.location.pathname : '/');
  useEffect(() => {
    if (!isBrowser) return;
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const navigate = (to: string) => { if (!isBrowser) return; window.history.pushState({}, '', to); setPath(to); };

  const theme = useMemo<ThemeMode>(()=>getTheme(),[]);
  const screensView = useMemo<ScreensView>(() => getScreensView(), []);
  const t = tw(theme);

  const scrollToScreens = () => { if(!isBrowser) return; const el = document.getElementById("screens"); el?.scrollIntoView({behavior:"smooth", block:"start"}); };

  useEffect(()=>{ try{ if(isBrowser && (document.documentElement as any) && (document.documentElement.style as any) && ("scrollBehavior" in document.documentElement.style === false)){ import("scroll-behavior-polyfill").catch(()=>{});} }catch{} },[]);

  const emailIsValid = useMemo(()=> /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim()), [email]);

  function openModal(){ setShowModal(true); setStatus(null); }
  function closeModal(){ setShowModal(false); setEmail(""); setConsent(false); setLoading(false); setStatus(null); }

  async function handleSubmit(e?:React.FormEvent){
    e?.preventDefault(); setStatus(null);
    const trimmed = email.trim();
    if(!trimmed){ setStatus({ok:false,msg:"Введите e‑mail."}); return; }
    if(!emailIsValid){ setStatus({ok:false,msg:"Проверьте формат e‑mail."}); return; }
    if(!consent){ setStatus({ok:false,msg:"Нужно согласие на обработку персональных данных."}); return; }
    setLoading(true);
    if(!WAITLIST_ENDPOINT){
      try{ const key="waitlist_local_submissions"; const existing=JSON.parse(localStorage.getItem(key)||"[]"); const record={email:trimmed, created_at:new Date().toISOString()}; localStorage.setItem(key, JSON.stringify([record,...existing])); setStatus({ok:false,msg:"Тестовый режим: заявка сохранена только на этом устройстве и не отправлена на сервер."}); }
      catch{ setStatus({ok:false,msg:"Не удалось отправить заявку: backend‑endpoint не настроен."}); }
      finally{ setLoading(false); }
      return;
    }
    try{ const res = await fetch(WAITLIST_ENDPOINT,{method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({email:trimmed})}); if(!res.ok) throw new Error("Bad response"); setStatus({ok:true,msg:"Спасибо! Вы в списке раннего доступа."}); setEmail(""); setConsent(false); }
    catch{ setStatus({ok:false,msg:"Не удалось отправить. Проверьте соединение или endpoint."}); }
    finally{ setLoading(false); }
  }

  function copyLocal(){ try{ const data = localStorage.getItem("waitlist_local_submissions") || "[]"; navigator.clipboard.writeText(data).then(()=>{ setCopied(true); setTimeout(()=>setCopied(false),1500); }); } catch{} }

  function ScreensSection({theme}:{theme:ThemeMode}){
    const hasReal = PREVIEW_IMAGES.length > 0;
    const showImages = hasReal;
    const t = tw(theme);
    return (
      <section id="screens" className={`py-8 sm:py-12 border-t ${t.sectionBorder}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4">
            <h2 className="text-2xl sm:text-4xl font-bold">Как выглядит ЛипидПро</h2>
          </div>
          {showImages ? (
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PREVIEW_IMAGES.slice(0,3).map((src,idx)=> (
                <div key={idx} className="relative">
                  {screensView === "iphone" ? (
                    <IPhoneDynamicIslandFrame src={src} alt={`Скриншот ${idx+1}`} theme={theme}/>
                  ) : (
                    <PlainScreenshotFrame src={src} alt={`Скриншот ${idx+1}`} theme={theme}/>
                  )}
                  <div className={`mt-3 text-sm text-center ${tw(theme).body}`}>{DEFAULT_CAPTIONS[idx] || `Скриншот ${idx+1}`}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </section>
    );
  }

  // ====== /privacy (SPA) ======
  if (path === '/privacy') {
    return (
      <div className={t.wrapper}>
        <header className={t.header}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="ЛипидПро" className="h-9 w-auto select-none" draggable={false} />
              <span className={`text-sm sm:text-base ${t.body}`}>ЛипидПро</span>
            </div>
            <button onClick={()=>navigate('/')} className={`rounded-2xl px-4 py-2 ${t.ghost}`}>← На главную</button>
          </div>
        </header>
        <section className={`py-8 sm:py-12 border-t ${t.sectionBorder}`}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl sm:text-5xl font-bold">Политика конфиденциальности</h1>
            <p className={`${t.muted} text-sm mt-2`}>Текущая версия политики. До публичного релиза будет опубликована финальная юридическая редакция.</p>
            <div className="mt-8 grid gap-4 text-sm">
              <div className={t.card}><div className="font-semibold">1. Какие данные собираем</div><p className={`mt-1 ${t.muted}`}>E‑mail (лист ожидания), профиль, показатели здоровья, технические логи.</p></div>
              <div className={t.card}><div className="font-semibold">2. Зачем</div><p className={`mt-1 ${t.muted}`}>Персонализация рекомендаций, уведомления, улучшение сервиса (анонимная аналитика).</p></div>
              <div className={t.card}><div className="font-semibold">3. Правовое основание</div><p className={`mt-1 ${t.muted}`}>Согласие пользователя и исполнение договора (оферты).</p></div>
              <div className={t.card}><div className="font-semibold">4. Передача третьим лицам</div><p className={`mt-1 ${t.muted}`}>Только по договору обработки и в рамках закона.</p></div>
              <div className={t.card}><div className="font-semibold">5. Хранение и безопасность</div><p className={`mt-1 ${t.muted}`}>HTTPS, шифрование, контроль доступа, аудит.</p></div>
              <div className={t.card}><div className="font-semibold">6. Права пользователя</div><p className={`mt-1 ${t.muted}`}>Доступ/исправление/удаление, отзыв согласия — по запросу.</p></div>
              <div className={t.card}><div className="font-semibold">7. Контакты</div><p className={`mt-1 ${t.muted}`}>Пишите нам: <a className="underline" href="mailto:team@lipidpro.app">team@lipidpro.app</a></p></div>
            </div>
          </div>
        </section>
        <footer className={`border-t ${t.sectionBorder}`}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className={`${t.muted} text-sm`}>© {new Date().getFullYear()} ЛипидПро</div>
            <div className={`flex items-center gap-6 text-sm ${t.navText}`}>
              <a href="/" onClick={(e)=>{e.preventDefault(); navigate('/');}} className="hover:opacity-100 hover:text-inherit">Главная</a>
              <a href="/privacy" onClick={(e)=>{e.preventDefault(); navigate('/privacy');}} className="hover:opacity-100 hover:text-inherit">Политика конфиденциальности</a>
              <a href="mailto:team@lipidpro.app" className="hover:opacity-100 hover:text-inherit">Связаться</a>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // ===== Главная страница =====
  return (
    <div className={t.wrapper}>
      <header className={t.header}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3"><img src="/logo.svg" alt="ЛипидПро" className="h-9 w-auto select-none" draggable={false} /><span className={`text-sm sm:text-base ${t.body}`}>ЛипидПро</span></div>
          <nav className={`hidden md:flex items-center gap-6 ${t.navText}`}><a className="hover:opacity-100 hover:text-inherit" href="#features">Преимущества</a><a className="hover:opacity-100 hover:text-inherit" href="#how">Как это работает</a><a className="hover:opacity-100 hover:text-inherit" href="#ai-demo">Примеры расшифровки</a><a className="hover:opacity-100 hover:text-inherit" href="#screens">Скриншоты</a><a className="hover:opacity-100 hover:text-inherit" href="#security">Безопасность</a></nav>
          <button onClick={openModal} className={`rounded-2xl px-4 py-2 font-semibold hover:brightness-95 active:scale-[.99] ${t.cta}`}>Ранний доступ</button>
        </div>
      </header>

      <section className="relative overflow-hidden">
        {theme==='dark' && (
          <div className="absolute inset-0 -z-10 opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]"><div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-lime-400/30 blur-3xl"/><div className="absolute top-1/2 -right-24 h-72 w-72 rounded-full bg-cyan-400/30 blur-3xl"/></div>
        )}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="max-w-3xl">
            <h1 className={`text-3xl sm:text-5xl font-extrabold leading-tight ${theme==='light' ? 'text-slate-900' : ''}`}>Контроль липидов и сердечно‑сосудистого риска —<span className={`block mt-2 ${theme==='light' ? 'text-green-600' : 'bg-gradient-to-r from-lime-300 to-cyan-300 bg-clip-text text-transparent'}`}>{theme==='light' ? 'в одном приложении' : ''}</span>{theme==='dark' ? '' : ''}</h1>
            <p className={`mt-5 text-base sm:text-lg ${t.muted}`}>Вносите анализы, получайте умные подсказки, ведите дневник питания и следите за прогрессом. Первым тестируйте «ЛипидПро» и помогайте нам сделать его идеальным.</p>
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"><button onClick={openModal} className={`rounded-2xl px-6 py-3 font-semibold hover:brightness-95 active:scale-[.99] ${t.cta}`}>Принять участие в раннем доступе</button><button onClick={scrollToScreens} className={`rounded-2xl px-6 py-3 ${t.ghost}`}>Узнать больше</button></div>
          </div>
        </div>
      </section>

      <section id="features" className={`py-8 sm:py-12 border-t ${t.sectionBorder}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-4xl font-bold">Почему это удобно</h2>
          <p className={`${t.muted} mt-2 max-w-3xl`}>Собрали ключевые возможности, которые экономят время и повышают приверженность лечению.</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {title:"Автоматическая расшифровка липидограммы", desc:"Понятное объяснение показателей: общий холестерин, ЛПНП, ЛПВП и триглицериды."},
              {title:"Контроль динамики показателей", desc:"Сравнивайте результаты анализов по датам и отслеживайте изменения в одном месте."},
              {title:"Дневник питания", desc:"Фиксируйте приёмы пищи и формируйте устойчивые привычки для контроля холестерина."},
              {title:"Показатели здоровья в одном экране", desc:"Вес, артериальное давление, липидный профиль и ключевые метрики без лишних переключений."},
              {title:"Напоминания и регулярность", desc:"Не пропускайте анализы, важные действия и плановые проверки состояния."},
              {title:"Защита персональных данных", desc:"Минимизация данных, контроль доступа и прозрачные правила обработки информации."},
            ].map((f,i)=>(
              <div key={i} className={`${theme==='light' ? 'rounded-2xl border border-slate-200 bg-white p-5 hover:bg-slate-50' : 'rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/[.07] transition-colors'}`}><div className={`${theme==='light' ? 'text-green-600' : 'text-lime-300'} text-sm font-semibold`}>{f.title}</div><div className={`mt-1 text-sm ${t.muted}`}>{f.desc}</div></div>
            ))}
          </div>
        </div>
      </section>

      <AIDemoSection theme={theme}/>
      <ScreensSection theme={theme}/>

      <section id="how" className={`py-8 sm:py-12 border-t ${t.sectionBorder}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-4xl font-bold">Как работает ЛипидПро</h2>
          <p className={`${t.muted} mt-2 max-w-3xl`}>От загрузки липидограммы до персональных рекомендаций по контролю холестерина и сердечно‑сосудистого риска.</p>
          <ol className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 list-decimal list-inside marker:text-green-600 marker:font-bold marker:text-lg">
            {[
              {title:"Добавьте показатели липидограммы", desc:"Введите ОХС, ЛПНП, ЛПВП и триглицериды из бланка анализа."},
              {title:"Получите понятную расшифровку", desc:"Приложение объяснит результаты простым языком и покажет динамику показателей."},
              {title:"Сформируйте ежедневный план", desc:"Ведите дневник питания, отслеживайте привычки и получайте персональные подсказки."},
              {title:"Контролируйте прогресс", desc:"Следите за изменениями и получайте напоминания, чтобы не выпадать из плана."},
            ].map((s,i)=>(
              <li key={i} className={`${t.card.replace('p-6','p-5')}`}><span className="text-lg font-bold text-green-600">{s.title}</span><div className={`text-sm mt-1 ${t.muted}`}>{s.desc}</div></li>
            ))}
          </ol>
          <p className={`mt-4 text-xs ${t.subtle}`}>Сервис помогает отслеживать показатели и привычки, но не заменяет консультацию врача.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3"><button onClick={openModal} className={`rounded-2xl px-6 py-3 font-semibold hover:brightness-95 ${t.cta}`}>Получить ранний доступ</button>{!WAITLIST_ENDPOINT && (<button onClick={copyLocal} className={`rounded-2xl px-6 py-3 ${t.ghost}`}>Экспорт локальных заявок {copied && <span className={theme==='light' ? 'ml-1 text-green-600' : 'ml-1 text-lime-300'}>скопировано</span>}</button>)}</div>
        </div>
      </section>

      {/* Security */}
      <section id="security" className={`py-8 sm:py-12 border-t ${t.sectionBorder}`}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-4xl font-bold">Безопасность и приватность</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2">
            <div className={`${t.card}`}><div className="font-semibold">Минимальный сбор данных</div><p className={`text-sm mt-1 ${t.muted}`}>Собираем только то, что нужно для работы функций. Прозрачная политика конфиденциальности.</p></div>
            <div className={`${t.card}`}><div className="font-semibold">Шифрование и защита данных</div><p className={`text-sm mt-1 ${t.muted}`}>Передача по HTTPS, контроль доступа и аудит. Политика и юридические документы финализируются перед релизом.</p></div>
          </div>
        </div>
      </section>

      <footer className={`border-t ${t.sectionBorder}`}>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className={`${t.muted} text-sm`}>© {new Date().getFullYear()} ЛипидПро</div>
          <div className={`flex items-center gap-6 text-sm ${t.navText}`}>
            <a href="#security" className="hover:opacity-100 hover:text-inherit">Безопасность</a>
            <a href="/privacy" onClick={(e)=>{e.preventDefault(); navigate('/privacy');}} className="hover:opacity-100 hover:text-inherit">Политика конфиденциальности</a>
            <a href="mailto:team@lipidpro.app" className="hover:opacity-100 hover:text-inherit">Связаться</a>
          </div>
        </div>
      </footer>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={closeModal}/>
          <div role="dialog" aria-modal="true" className={`relative w-full max-w-lg rounded-2xl border p-6 shadow-2xl ${theme==='light' ? 'border-slate-200 bg-white' : 'border-white/10 bg-slate-900'}`}>
            <h3 className="text-xl font-semibold">Участвовать в раннем тестировании</h3>
            <p className={`text-sm mt-1 ${t.muted}`}>Оставьте e‑mail — пришлём приглашение, как только приложение станет доступно.</p>
            <form ref={formRef} onSubmit={handleSubmit} className="mt-5 space-y-3">
              <label className="block"><span className="text-sm">E‑mail</span><input type="email" required inputMode="email" placeholder="you@example.com" className={`mt-1 w-full rounded-xl px-4 py-3 outline-none focus:ring-2 ${theme==='light' ? 'bg-white border border-slate-300 focus:ring-green-300' : 'bg-white/5 border border-white/10 focus:ring-lime-300'}`} value={email} onChange={(e)=>setEmail(e.target.value)}/></label>
              <label className={`flex items-start gap-3 text-sm ${t.muted}`}><input type="checkbox" checked={consent} onChange={(e)=>setConsent(e.target.checked)} className="mt-1 h-4 w-4"/><span>Даю согласие на обработку персональных данных согласно <a href="/privacy" onClick={(e)=>{e.preventDefault(); setShowModal(false); navigate('/privacy');}} className="underline decoration-dotted">Политике конфиденциальности</a>.</span></label>
              {status && (<div className={`text-sm ${status.ok? (theme==='light'?'text-green-600':'text-lime-300'):'text-rose-500'}`}>{status.msg}</div>)}
              <div className="flex items-center gap-3 pt-1"><button type="submit" disabled={loading} className={`rounded-2xl px-6 py-3 font-semibold hover:brightness-95 disabled:opacity-60 ${t.cta}`}>{loading? 'Отправляем…':'Отправить'}</button><button type="button" onClick={closeModal} className={`rounded-2xl px-6 py-3 ${t.ghost}`}>Отменить</button></div>
            </form>
            {!WAITLIST_ENDPOINT && (<div className={`mt-4 text-xs ${t.subtle}`}>Тестовый режим: backend‑endpoint не настроен. Заявки сохраняются только локально (LocalStorage → waitlist_local_submissions).</div>)}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== DEV‑утилиты и тесты =====
function computeKcal(p:number,f:number,c:number){ return Math.round(p*4 + f*9 + c*4); }
function computeStreak(dates:string[]):number{ const set=new Set(dates.map(d=>new Date(d).toDateString())); const arr=Array.from(set).map(d=>new Date(d)).sort((a,b)=>a.getTime()-b.getTime()); let best=0,cur=0; for(let i=0;i<arr.length;i++){ if(i===0){cur=1;best=1;continue;} const diff=(arr[i].getTime()-arr[i-1].getTime())/(1000*60*60*24); if(diff===1)cur++; else cur=1; if(cur>best)best=cur;} return best; }
function getMacroColorClass(label:string):string{ if(label==='Б')return 'bg-emerald-400'; if(label==='Ж')return 'bg-amber-400'; return 'bg-sky-400'; }

function runDevTests(){ try{
  const log=(name:string,ok:boolean)=>console[ok? 'log':'error'](`[TEST] ${name}: ${ok? 'OK':'FAIL'}`);
  const rx=/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/; const valid=["a@b.co","user.name+tag@example.com","alex@yurin.ru"]; const invalid=["","user@","@d.com","bad@@example.com","user@localhost"];
  log('email valid', valid.every(e=>rx.test(e))); log('email invalid', invalid.every(e=>!rx.test(e)));
  log('firstNonEmpty', firstNonEmpty("", " ", "x","y") === "x"); log('firstNonEmpty empty', firstNonEmpty("", " ") === "");
  const imgs=getPreviewImages(6); log('preview array', Array.isArray(imgs)&&imgs.every(i=>typeof i==='string'));
  const demos=buildAIDemoSamples(); log('ai demos count 3', demos.length===3); log('ai score bounds', demos.every(d=>d.score>=0&&d.score<=100));
  log('computeKcal 30/20/40 = 460', computeKcal(30,20,40)===460); log('computeKcal zeros', computeKcal(0,0,0)===0);
  log('streak 3 days', computeStreak(["2025-01-01","2025-01-02","2025-01-03"])===3); log('streak gap=2', computeStreak(["2025-01-01","2025-01-03","2025-01-04"])===2);
  log('macro Б emerald', getMacroColorClass('Б')==='bg-emerald-400'); log('macro Ж amber', getMacroColorClass('Ж')==='bg-amber-400'); log('macro У sky', getMacroColorClass('У')==='bg-sky-400');
  log('macro default sky', getMacroColorClass('X' as any)==='bg-sky-400');
  log('dish photo url', /^https?:\/\//.test(DISH_PHOTO));
  // Режимы
  const m1 = getScreensMode(); log('screens mode default is chat', m1==='chat' || m1==='images');
  const sv = getScreensView(); log('screens view default is plain|iphone', sv==='plain' || sv==='iphone');
  const th = getTheme(); log('theme is dark|light', th==='dark' || th==='light');
  if (isBrowser) {
    (window as any).__SCREENS_MODE__ = 'images'; log('screens mode can be overridden', getScreensMode()==='images'); (window as any).__SCREENS_MODE__ = undefined;
    (window as any).__SCREENS_VIEW__ = 'iphone'; log('screens view can be overridden', getScreensView()==='iphone'); (window as any).__SCREENS_VIEW__ = undefined;
  }
} catch(e){ console.error('[TEST] Unexpected', e);} }
if(isBrowser){ try{ const p=new URLSearchParams(window.location.search); if(p.has('test')) runDevTests(); }catch{} }
